import { c as _c } from "react/compiler-runtime";
import { Calendar, ChevronDown, ChevronRight, Keyboard } from "lucide-react";
import { AppSidebarHeader, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { cn } from "@repo/blacksmith-ui/cn";
import { SidebarPrimaryNavStatic } from "./SidebarPrimaryNav";
import { SESSION_FOOTER_CLASS, SESSION_FOOTER_LABEL_CLASS, SESSION_ITEM_INDICATOR_GUTTER_CLASS, SESSION_ITEM_ROW_CLASS, SESSION_ITEM_ROW_WRAP_CLASS, SESSION_SECTION_HEADER_BOX_CLASS } from "./sessionSidebarChrome";
import { COMPACT_SESSION_FOOTER_CLASS, COMPACT_SESSION_ITEM_INDICATOR_GUTTER_CLASS, COMPACT_SESSION_ITEM_ROW_CLASS, COMPACT_SESSION_SECTION_HEADER_BOX_CLASS } from "./compactSessionRowStyles";
import { readLeftSidebarSkeletonLayout, readSkeletonAutomationRows, useIsHydrated } from "./skeletonLayout";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
export const SESSION_SIDEBAR_EDGE_CLASS = "shadow-[inset_-1px_0_0_0_var(--color-border)]";
interface SidebarSkeletonProps {
  variant?: "full" | "inline";
  sidebarVariant?: CodesmithSidebarVariant;
  /** Automations group rows (`null`: no section); defaults to the URL org's last visit. */
  automationRows?: number | null;
}
const TITLE_WIDTHS = ["w-[78%]", "w-[55%]", "w-[68%]", "w-[44%]", "w-[62%]"];
const AUTOMATION_TITLE_WIDTHS = ["w-[60%]", "w-[48%]", "w-[54%]", "w-[42%]"];
function SessionItemSkeleton(t0) {
  const $ = _c(17);
  const {
    showAuthor,
    titleWidth,
    compact
  } = t0;
  if (compact) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={COMPACT_SESSION_ITEM_INDICATOR_GUTTER_CLASS} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    const t2 = `h-3.5 ${titleWidth} rounded`;
    let t3;
    if ($[1] !== t2) {
      t3 = <div className="flex h-[22px] min-w-0 flex-1 items-center"><Skeleton className={t2} /></div>;
      $[1] = t2;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="h-3 w-5 shrink-0 rounded" />;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== t3) {
      t5 = <div className={COMPACT_SESSION_ITEM_ROW_CLASS} data-slot="session-item-skeleton">{t1}<div className="flex min-w-0 flex-1 items-center gap-2">{t3}{t4}</div></div>;
      $[4] = t3;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={SESSION_ITEM_INDICATOR_GUTTER_CLASS} />;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = `h-3.5 ${titleWidth} rounded`;
  let t3;
  if ($[7] !== t2) {
    t3 = <div className="flex h-[22px] items-center"><Skeleton className={t2} /></div>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== showAuthor) {
    t4 = showAuthor && <Skeleton className="h-2.5 w-10 rounded" />;
    $[9] = showAuthor;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-2.5 w-12 rounded" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <div className="flex h-3 items-center gap-2">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className={SESSION_ITEM_ROW_WRAP_CLASS}><div className={SESSION_ITEM_ROW_CLASS} data-slot="session-item-skeleton">{t1}<div className="min-w-0 flex-1 overflow-hidden">{t3}{t6}</div></div></div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/** Collapsed AutomationSessionGroupItem header: chevron gutter, name, recency + calendar. */
function AutomationGroupSkeleton(t0) {
  const $ = _c(14);
  const {
    titleWidth,
    compact
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChevronRight className="size-3.5 text-neutral-500 dark:text-neutral-400" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const chevron = t1;
  if (compact) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className={COMPACT_SESSION_ITEM_INDICATOR_GUTTER_CLASS}>{chevron}</div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Calendar className="size-3 shrink-0 text-neutral-500 dark:text-neutral-400" />;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    const t4 = `h-3.5 ${titleWidth} rounded`;
    let t5;
    if ($[3] !== t4) {
      t5 = <div className="flex h-[22px] min-w-0 flex-1 items-center"><Skeleton className={t4} /></div>;
      $[3] = t4;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Skeleton className="h-3 w-5 shrink-0 rounded" />;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] !== t5) {
      t7 = <div className={COMPACT_SESSION_ITEM_ROW_CLASS} data-slot="automation-group-skeleton">{t2}<div className="flex min-w-0 flex-1 items-center gap-1">{t3}{t5}{t6}</div></div>;
      $[6] = t5;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    return t7;
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex w-4 shrink-0 items-center justify-center py-[5px]">{chevron}</div>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = `h-3.5 ${titleWidth} rounded`;
  let t4;
  if ($[9] !== t3) {
    t4 = <div className="flex h-[22px] items-center"><Skeleton className={t3} /></div>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex h-3 items-center gap-1.5"><Skeleton className="h-2.5 w-16 rounded" /><Calendar className="size-3 shrink-0 text-neutral-500 dark:text-neutral-400" /></div>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <div className={SESSION_ITEM_ROW_CLASS} data-slot="automation-group-skeleton">{t2}<div className="min-w-0 flex-1 overflow-hidden">{t4}{t5}</div></div>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function SectionHeaderSkeleton(t0) {
  const $ = _c(16);
  const {
    label,
    first,
    compact
  } = t0;
  if (compact) {
    const t1 = !first && "mt-4";
    let t2;
    if ($[0] !== t1) {
      t2 = cn("h-9 pb-1", t1, COMPACT_SESSION_SECTION_HEADER_BOX_CLASS);
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== label) {
      t3 = <span className="type-body-xs-medium uppercase text-neutral-500 dark:text-neutral-400">{label}</span>;
      $[2] = label;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ChevronDown className="size-3 text-neutral-500 dark:text-neutral-400" aria-hidden={true} />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t2 || $[6] !== t3) {
      t5 = <div className={t2}>{t3}{t4}</div>;
      $[5] = t2;
      $[6] = t3;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  const t1 = first ? "pt-3" : "pt-5";
  let t2;
  if ($[8] !== t1) {
    t2 = cn(SESSION_SECTION_HEADER_BOX_CLASS, t1);
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== label) {
    t3 = <span className="type-uppercase-title text-neutral-500 dark:text-neutral-300">{label}</span>;
    $[10] = label;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown className="size-3 text-neutral-500 dark:text-neutral-400" aria-hidden={true} />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function SidebarBodyContent(t0) {
  const $ = _c(18);
  const {
    compact,
    automationRows
  } = t0;
  let t1;
  let t2;
  if ($[0] !== compact) {
    t1 = <SectionHeaderSkeleton label="My Sessions" first={true} compact={compact} />;
    t2 = TITLE_WIDTHS.map((w, i) => <SessionItemSkeleton key={`my-${i}`} titleWidth={w} compact={compact} />);
    $[0] = compact;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== automationRows || $[4] !== compact) {
    t3 = automationRows !== null && <><SectionHeaderSkeleton label="Automations" first={false} compact={compact} />{Array.from({
        length: automationRows
      }, (_, i_0) => <AutomationGroupSkeleton key={`automation-${i_0}`} titleWidth={AUTOMATION_TITLE_WIDTHS[i_0 % AUTOMATION_TITLE_WIDTHS.length]} compact={compact} />)}</>;
    $[3] = automationRows;
    $[4] = compact;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== compact) {
    t4 = compact && <SectionHeaderSkeleton label="Collaborating" first={false} compact={compact} />;
    $[6] = compact;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== compact) {
    t5 = <SectionHeaderSkeleton label="Team Sessions" first={false} compact={compact} />;
    t6 = TITLE_WIDTHS.map((w_0, i_1) => <SessionItemSkeleton key={`team-${i_1}`} showAuthor={true} titleWidth={w_0} compact={compact} />);
    $[8] = compact;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <>{t1}{t2}{t3}{t4}{t5}{t6}</>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function SidebarBody(t0) {
  const $ = _c(9);
  const {
    compact,
    automationRows
  } = t0;
  const t1 = compact ? "px-2 pb-1" : "px-2 py-1";
  let t2;
  if ($[0] !== automationRows || $[1] !== compact) {
    t2 = <SidebarBodyContent compact={compact} automationRows={automationRows} />;
    $[0] = automationRows;
    $[1] = compact;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <div className="h-full overflow-hidden"><div className={t1}>{t2}</div></div>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--sidebar)] to-transparent" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <div className="flex-1 relative min-h-0">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export function SidebarSkeleton(t0) {
  const $ = _c(27);
  const {
    variant: t1,
    sidebarVariant,
    automationRows
  } = t0;
  const variant = t1 === undefined ? "full" : t1;
  const isHydrated = useIsHydrated();
  let t2;
  if ($[0] !== isHydrated || $[1] !== variant) {
    t2 = variant === "full" && isHydrated ? readLeftSidebarSkeletonLayout() : null;
    $[0] = isHydrated;
    $[1] = variant;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const layout = t2;
  const resolvedVariant = sidebarVariant ?? layout?.variant ?? "legacy";
  const compact = resolvedVariant === "redesign";
  let t3;
  if ($[3] !== automationRows || $[4] !== isHydrated) {
    t3 = automationRows !== undefined ? automationRows : isHydrated ? readSkeletonAutomationRows() : null;
    $[3] = automationRows;
    $[4] = isHydrated;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const resolvedAutomationRows = t3;
  if (variant === "inline") {
    let t4;
    if ($[6] !== compact || $[7] !== resolvedAutomationRows) {
      t4 = <SidebarBodyContent compact={compact} automationRows={resolvedAutomationRows} />;
      $[6] = compact;
      $[7] = resolvedAutomationRows;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (layout && !layout.visible) {
    return null;
  }
  let t4;
  if ($[9] !== layout) {
    t4 = layout ? {
      width: layout.width
    } : undefined;
    $[9] = layout;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = layout ? "flex" : "hidden min-[1018px]:flex w-[330px]";
  let t6;
  if ($[11] !== t5) {
    t6 = cn("relative flex-col bg-sidebar overflow-hidden pointer-events-none flex-shrink-0", SESSION_SIDEBAR_EDGE_CLASS, t5);
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AppSidebarHeader title="[code]smith" actions={<PanelTogglePlaceholder />} />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== resolvedVariant) {
    t8 = <SidebarPrimaryNavStatic variant={resolvedVariant} />;
    $[14] = resolvedVariant;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== compact || $[17] !== resolvedAutomationRows) {
    t9 = <SidebarBody compact={compact} automationRows={resolvedAutomationRows} />;
    $[16] = compact;
    $[17] = resolvedAutomationRows;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== compact) {
    t10 = compact ? <div className={COMPACT_SESSION_FOOTER_CLASS}><span className="flex size-8 items-center justify-center text-neutral-500 dark:text-neutral-400"><Keyboard className="size-4" /></span><span className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">Archived</span></div> : <div className={SESSION_FOOTER_CLASS}><span className={SESSION_FOOTER_LABEL_CLASS}>Archived</span></div>;
    $[19] = compact;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t4 || $[23] !== t6 || $[24] !== t8 || $[25] !== t9) {
    t11 = <div style={t4} className={t6} data-slot="assistant-sidebar-skeleton">{t7}{t8}{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t4;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}